import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Spinner, Text } from "@fluentui/react-components";
import { PlanDataService } from "../services/PlanDataService";
import { ProcessedPlanData, WebsocketMessageType, MPlanData, AgentMessageData, AgentMessageType, ParsedUserClarification, AgentType, PlanStatus, TeamConfig } from "../models";
import PlanChat from "../components/content/PlanChat";
import PlanPanelRightTabs from "../components/content/PlanPanelRightTabs";
import PlanPanelLeft from "../components/content/PlanPanelLeft";
import { IntegrationsSidebar, ModelsSidebar } from "../components/content/WorkspaceSidebars";
import CoralShellColumn from "../coral/components/Layout/CoralShellColumn";
import CoralShellRow from "../coral/components/Layout/CoralShellRow";
import Content from "../coral/components/Content/Content";
import ContentToolbar from "../coral/components/Content/ContentToolbar";
import {
    useInlineToaster,
} from "../components/toast/InlineToaster";
import Octo from "../coral/imports/Octopus.png";
import PanelRightToggles from "../coral/components/Header/PanelRightToggles";
import { TaskListSquareLtr } from "../coral/imports/bundleicons";
import LoadingMessage, { loadingMessages } from "../coral/components/LoadingMessage";
import webSocketService from "../services/WebSocketService";
import { APIService } from "../api/apiService";
import { StreamMessage, StreamingPlanUpdate } from "../models";
import { usePlanCancellationAlert } from "../hooks/usePlanCancellationAlert";
import PlanCancellationDialog from "../components/common/PlanCancellationDialog";
import { safeGet, typeSafeAccess, SafePropertyAccess } from "../utils/safePropertyAccess";

import "../styles/PlanPage.css"

// Create API service instance
const apiService = new APIService();

/**
 * Page component for displaying a specific plan
 * Accessible via the route /plan/{plan_id}
 */
const PlanPage: React.FC = () => {
    const { planId } = useParams<{ planId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast, dismissToast } = useInlineToaster();
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const [input, setInput] = useState<string>("");
    const [planData, setPlanData] = useState<ProcessedPlanData | any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [submittingChatDisableInput, setSubmittingChatDisableInput] = useState<boolean>(true);
    const [errorLoading, setErrorLoading] = useState<boolean>(false);
    const [clarificationMessage, setClarificationMessage] = useState<ParsedUserClarification | null>(null);
    const [processingApproval, setProcessingApproval] = useState<boolean>(false);
    const [planApprovalRequest, setPlanApprovalRequest] = useState<MPlanData | null>(null);
    const [reloadLeftList, setReloadLeftList] = useState<boolean>(true);
    const [waitingForPlan, setWaitingForPlan] = useState<boolean>(true);
    const [showProcessingPlanSpinner, setShowProcessingPlanSpinner] = useState<boolean>(false);
    const [showApprovalButtons, setShowApprovalButtons] = useState<boolean>(true);
    const [continueWithWebsocketFlow, setContinueWithWebsocketFlow] = useState<boolean>(false);
    const [selectedTeam, setSelectedTeam] = useState<TeamConfig | null>(null);
    // WebSocket connection state
    const [wsConnected, setWsConnected] = useState<boolean>(false);
    const [streamingMessages, setStreamingMessages] = useState<StreamingPlanUpdate[]>([]);
    const [streamingMessageBuffer, setStreamingMessageBuffer] = useState<string>("");
    const [showBufferingText, setShowBufferingText] = useState<boolean>(false);
    const [agentMessages, setAgentMessages] = useState<AgentMessageData[]>([]);

    // Plan approval state - track when plan is approved
    const [planApproved, setPlanApproved] = useState<boolean>(false);
    
    // Workflow completion state - track when workflow is completed
    const [workflowCompleted, setWorkflowCompleted] = useState<boolean>(false);
    
    // Debug: Track showProcessingPlanSpinner changes
    useEffect(() => {
        console.log('🔄 showProcessingPlanSpinner changed to:', showProcessingPlanSpinner, 'workflowCompleted:', workflowCompleted);
    }, [showProcessingPlanSpinner, workflowCompleted]);
    
    // CRITICAL: Enforce spinner state when workflow is completed
    useEffect(() => {
        if (workflowCompleted && showProcessingPlanSpinner) {
            console.log('🚨 ENFORCING: Workflow completed but spinner is still on - turning it off');
            setShowProcessingPlanSpinner(false);
        }
    }, [workflowCompleted, showProcessingPlanSpinner]);
    
    // Debug: Track reloadLeftList changes
    useEffect(() => {
        console.log('📋 reloadLeftList changed to:', reloadLeftList);
    }, [reloadLeftList]);
    
    // Timeout-based fallback for processing spinner
    const spinnerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Debounce refresh to prevent polling behavior
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Plan cancellation dialog state
    const [showCancellationDialog, setShowCancellationDialog] = useState<boolean>(false);
    const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
    const [cancellingPlan, setCancellingPlan] = useState<boolean>(false);

    const [loadingMessage, setLoadingMessage] = useState<string>(loadingMessages[0]);

    // Plan cancellation alert hook
    const { isPlanActive, handleNavigationWithConfirmation } = usePlanCancellationAlert({
        planData,
        planApprovalRequest,
        onNavigate: pendingNavigation || (() => {})
    });

    // Handle navigation with plan cancellation check
    const handleNavigationWithAlert = useCallback((navigationFn: () => void) => {
        if (!isPlanActive()) {
            // Plan is not active, proceed with navigation
            navigationFn();
            return;
        }

        // Plan is active, show confirmation dialog
        setPendingNavigation(() => navigationFn);
        setShowCancellationDialog(true);
    }, [isPlanActive]);

    // Handle confirmation dialog response
    const handleConfirmCancellation = useCallback(async () => {
        setCancellingPlan(true);
        
        try {
            if (planApprovalRequest?.id) {
                await apiService.approvePlan({
                    m_plan_id: planApprovalRequest.id,
                    plan_id: planData?.plan?.id,
                    approved: false,
                    feedback: 'Plan cancelled by user navigation'
                });
            }

            // Execute the pending navigation
            if (pendingNavigation) {
                pendingNavigation();
            }
        } catch (error) {
            console.error('❌ Failed to cancel plan:', error);
            showToast('Failed to cancel the plan properly, but navigation will continue.', 'error');
            // Still proceed with navigation even if cancellation failed
            if (pendingNavigation) {
                pendingNavigation();
            }
        } finally {
            setCancellingPlan(false);
            setShowCancellationDialog(false);
            setPendingNavigation(null);
        }
    }, [planApprovalRequest, planData, pendingNavigation, showToast]);

    const handleCancelDialog = useCallback(() => {
        setShowCancellationDialog(false);
        setPendingNavigation(null);
    }, []);



    // Debounced refresh function to prevent polling
    const debouncedRefresh = useCallback(() => {
        console.log('🔄 debouncedRefresh called - will trigger history refresh in 1 second');
        // Clear any existing timeout
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }
        
        // Set a new timeout
        refreshTimeoutRef.current = setTimeout(() => {
            console.log('🔄 Setting reloadLeftList to true - triggering history refresh');
            // Force a toggle to ensure the left panel detects the change
            setReloadLeftList(false);
            setTimeout(() => {
                setReloadLeftList(true);
            }, 50);
            refreshTimeoutRef.current = null;
        }, 1000);
    }, []);

    const processAgentMessage = useCallback((agentMessageData: AgentMessageData, planData: ProcessedPlanData, is_final: boolean = false, streaming_message: string = '', fromWebSocket: boolean = false) => {

        // CRITICAL FIX: Don't persist agent messages that come from WebSocket to prevent infinite loop
        // Only persist messages that originate from the frontend (like user clarifications)
        if (fromWebSocket) {
            console.log('📋 Agent message from WebSocket - skipping API persistence to prevent infinite loop');
            
            // If this is a final message, refresh the task list
            if (is_final) {
                // Use debounced refresh to prevent multiple rapid refreshes
                debouncedRefresh();
            }
            
            return Promise.resolve();
        }

        // Persist / forward to backend (fire-and-forget with logging) - only for frontend-originated messages
        const agentMessageResponse = PlanDataService.createAgentMessageResponse(agentMessageData, planData, is_final, streaming_message);
        console.log('📤 Persisting frontend-originated agent message:', agentMessageResponse);
        const sendPromise = apiService.sendAgentMessage(agentMessageResponse)
            .then(saved => {
                console.log('[agent_message][persisted]', {
                    agent: agentMessageData.agent,
                    type: agentMessageData.agent_type,
                    ts: agentMessageData.timestamp
                });
                
                // If this is a final message, refresh the task list after successful persistence
                if (is_final) {
                    // Use debounced refresh to prevent multiple rapid refreshes
                    debouncedRefresh();
                }
            })
            .catch(err => {
                console.warn('[agent_message][persist-failed]', err);
                // Even if persistence fails, still refresh the task list for final messages
                // The local plan data has been updated, so the UI should reflect that
                if (is_final) {
                    // Use debounced refresh to prevent multiple rapid refreshes
                    debouncedRefresh();
                }
            });

        return sendPromise;

    }, [debouncedRefresh]);

    const resetPlanVariables = useCallback(() => {
        // Clear any pending refresh timeouts when resetting
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }
        
        setInput("");
        setPlanData(null);
        setLoading(true);
        setSubmittingChatDisableInput(true);
        setErrorLoading(false);
        setClarificationMessage(null);
        setProcessingApproval(false);
        setPlanApprovalRequest(null);
        setReloadLeftList(true);
        setWaitingForPlan(true);
        setShowProcessingPlanSpinner(false);
        setShowApprovalButtons(true);
        setContinueWithWebsocketFlow(false);
        setWsConnected(false);
        setStreamingMessages([]);
        setStreamingMessageBuffer("");
        setShowBufferingText(false);
        setAgentMessages([]);
        setWorkflowCompleted(false);
        
        // Clear spinner timeout
        if (spinnerTimeoutRef.current) {
            clearTimeout(spinnerTimeoutRef.current);
            spinnerTimeoutRef.current = null;
        }
    }, [
        setInput,
        setPlanData,
        setLoading,
        setSubmittingChatDisableInput,
        setErrorLoading,
        setClarificationMessage,
        setProcessingApproval,
        setPlanApprovalRequest,
        setReloadLeftList,
        setWaitingForPlan,
        setShowProcessingPlanSpinner,
        setShowApprovalButtons,
        setContinueWithWebsocketFlow,
        setWsConnected,
        setStreamingMessages,
        setStreamingMessageBuffer,
        setShowBufferingText,
        setAgentMessages
    ]);

    // Throttled auto-scroll helper to prevent fast scrolling during high-frequency agent messages
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastScrollTime = useRef<number>(0);
    const SCROLL_THROTTLE_MS = 5000; // Only auto-scroll once every 5 seconds
    
    const scrollToBottom = useCallback(() => {
        const now = Date.now();
        const timeSinceLastScroll = now - lastScrollTime.current;
        
        // If we recently scrolled, schedule a delayed scroll instead of immediate
        if (timeSinceLastScroll < SCROLL_THROTTLE_MS) {
            // Clear any existing timeout
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            
            // Schedule a scroll for when the throttle period expires
            const remainingTime = SCROLL_THROTTLE_MS - timeSinceLastScroll;
            scrollTimeoutRef.current = setTimeout(() => {
                performScroll();
                scrollTimeoutRef.current = null;
            }, remainingTime);
            return;
        }
        
        // Immediate scroll if enough time has passed
        performScroll();
    }, []);
    
    const performScroll = useCallback(() => {
        lastScrollTime.current = Date.now();
        setTimeout(() => {
            if (messagesContainerRef.current) {
                messagesContainerRef.current?.scrollTo({
                    top: messagesContainerRef.current.scrollHeight,
                    behavior: "smooth",
                });
            }
        }, 100);
    }, []);

    // Create loadPlanData function with useCallback to memoize it
    const isFreshlyCreated = location.state?.freshlyCreated === true;

    const loadPlanData = useCallback(
        async (useCache = true): Promise<ProcessedPlanData | null> => {
            if (!planId) return null;
            resetPlanVariables();
            setLoading(true);
            try {

                let planResult: ProcessedPlanData | null = null;
                console.log("Fetching plan with ID:", planId);
                planResult = await PlanDataService.fetchPlanData(planId, useCache);
                console.log("Plan data fetched:", planResult);
                // Show approval buttons for plans that need approval (CREATED status)
                // Hide approval buttons for plans that are executing (IN_PROGRESS) or completed
                const planStatus = safeGet(planResult, 'plan.overall_status', null);
                if (planStatus === PlanStatus.CREATED) {
                    setShowApprovalButtons(true);
                    setWaitingForPlan(false); // Plan is ready for approval, not waiting
                    setSubmittingChatDisableInput(false); // Enable chat for created plans
                } else if (planStatus === PlanStatus.IN_PROGRESS) {
                    setShowApprovalButtons(false);
                    setWaitingForPlan(false); // Plan is executing
                    setSubmittingChatDisableInput(true); // Disable chat during execution
                } else {
                    setShowApprovalButtons(false);
                    setWaitingForPlan(false);
                    // Enable chat for completed, failed, or canceled plans
                    setSubmittingChatDisableInput(false);
                }
                if (planStatus !== PlanStatus.COMPLETED) {
                    setContinueWithWebsocketFlow(true);
                }
                
                const messages = safeGet(planResult, 'messages', []);
                if (messages.length > 0) {
                    setAgentMessages(messages);
                }
                
                const mplan = safeGet(planResult, 'mplan', null);
                if (mplan) {
                    setPlanApprovalRequest(mplan);
                }
                
                const streamingMessage = typeSafeAccess.string(planResult, 'streaming_message', '');
                if (streamingMessage.trim() !== "") {
                    setStreamingMessageBuffer(streamingMessage);
                    setShowBufferingText(true);
                }
                setPlanData(planResult);
                
                // Set the selected team from plan data for dynamic UI
                if (planResult?.team) {
                    setSelectedTeam(planResult.team);
                }
                
                return planResult;
            } catch (err: any) {
                // Retry with exponential backoff for 404 errors on freshly created plans
                if (err?.status === 404 && isFreshlyCreated) {
                    console.log("Plan not found yet (freshly created), retrying with backoff...");
                    const backoffDelays = [1000, 2000, 4000]; // 1s, 2s, 4s
                    const maxRetryTime = 30000; // 30 seconds total
                    const startTime = Date.now();

                    for (const delay of backoffDelays) {
                        if (Date.now() - startTime >= maxRetryTime) break;
                        await new Promise((r) => setTimeout(r, delay));
                        try {
                            const retryResult = await PlanDataService.fetchPlanData(planId, false);
                            console.log("Plan data fetched on retry:", retryResult);
                            const planStatus = safeGet(retryResult, 'plan.overall_status', null);
                            if (planStatus === PlanStatus.CREATED) {
                                setShowApprovalButtons(true);
                                setWaitingForPlan(false);
                                setSubmittingChatDisableInput(false);
                            } else if (planStatus === PlanStatus.IN_PROGRESS) {
                                setShowApprovalButtons(false);
                                setWaitingForPlan(false);
                                setSubmittingChatDisableInput(true);
                            } else {
                                setShowApprovalButtons(false);
                                setWaitingForPlan(false);
                                setSubmittingChatDisableInput(false);
                            }
                            if (planStatus !== PlanStatus.COMPLETED) {
                                setContinueWithWebsocketFlow(true);
                            }
                            const messages = safeGet(retryResult, 'messages', []);
                            if (messages.length > 0) setAgentMessages(messages);
                            const mplan = safeGet(retryResult, 'mplan', null);
                            if (mplan) setPlanApprovalRequest(mplan);
                            const streamingMessage = typeSafeAccess.string(retryResult, 'streaming_message', '');
                            if (streamingMessage.trim() !== "") {
                                setStreamingMessageBuffer(streamingMessage);
                                setShowBufferingText(true);
                            }
                            setPlanData(retryResult);
                            if (retryResult?.team) setSelectedTeam(retryResult.team);
                            setLoading(false);
                            return retryResult;
                        } catch (retryErr: any) {
                            if (retryErr?.status !== 404) {
                                // Non-404 error during retry — fail immediately
                                console.log("Non-404 error during retry:", retryErr);
                                setErrorLoading(true);
                                setPlanData(null);
                                setLoading(false);
                                return null;
                            }
                            console.log(`Retry failed with 404, next backoff...`);
                        }
                    }
                    // All retries exhausted
                    console.log("All retries exhausted for freshly created plan");
                }
                console.log("Failed to load plan data:", err);
                setErrorLoading(true);
                setPlanData(null);
                return null;
            } finally {
                setLoading(false);
            }
        },
        [planId, navigate, resetPlanVariables, isFreshlyCreated]
    );

    //WebsocketMessageType.PLAN_APPROVAL_REQUEST
    useEffect(() => {
        const unsubscribe = webSocketService.on(WebsocketMessageType.PLAN_APPROVAL_REQUEST, async (approvalRequest: any) => {
            console.log('📋 Plan received:', approvalRequest);

            let mPlanData: MPlanData | null = null;

            // Handle the different message structures
            if (approvalRequest.parsedData) {
                // Direct parsedData property
                mPlanData = approvalRequest.parsedData;
            } else if (approvalRequest.data && typeof approvalRequest.data === 'object') {
                // Data property with nested object
                if (approvalRequest.data.parsedData) {
                    mPlanData = approvalRequest.data.parsedData;
                } else {
                    // Try to parse the data object directly
                    mPlanData = approvalRequest.data;
                }
            } else if (approvalRequest.rawData) {
                // Parse the raw data string
                mPlanData = PlanDataService.parsePlanApprovalRequest(approvalRequest.rawData);
            } else {
                // Try to parse the entire object
                mPlanData = PlanDataService.parsePlanApprovalRequest(approvalRequest);
            }

            if (mPlanData) {
                console.log('✅ Parsed plan data:', mPlanData);
                setPlanApprovalRequest(mPlanData);
                setWaitingForPlan(false);
                setShowProcessingPlanSpinner(false);
                
                // Show approval buttons when we receive a plan approval request
                // This indicates the plan is ready for user approval
                setShowApprovalButtons(true);
                
                // Note: We don't need to call loadPlanData here because:
                // 1. The plan approval request already contains the necessary plan data
                // 2. WebSocket messages will provide real-time updates
                // 3. Calling loadPlanData causes unnecessary UI flickering by resetting state
                console.log('📋 Plan approval request processed, using WebSocket data for real-time updates');
                
                scrollToBottom();
            } else {
                console.error('❌ Failed to parse plan data', approvalRequest);
            }
        });

        return () => unsubscribe();
    }, [scrollToBottom, planId, planData, loadPlanData]);

    //(WebsocketMessageType.AGENT_MESSAGE_STREAMING
    useEffect(() => {
        const unsubscribe = webSocketService.on(WebsocketMessageType.AGENT_MESSAGE_STREAMING, (streamingMessage: any) => {
            //console.log('📋 Streaming Message', streamingMessage);
            // if is final true clear buffer and add final message to agent messages
            const line = PlanDataService.simplifyHumanClarification(streamingMessage.data.content);
            setShowBufferingText(true);
            setStreamingMessageBuffer(prev => prev + line);
            //scrollToBottom();

        });

        return () => unsubscribe();
    }, [scrollToBottom]);

    //WebsocketMessageType.USER_CLARIFICATION_REQUEST
    useEffect(() => {
        const unsubscribe = webSocketService.on(WebsocketMessageType.USER_CLARIFICATION_REQUEST, (clarificationMessage: any) => {
            console.log('📋 Clarification Message', clarificationMessage);
            console.log('📋 Current plan data User clarification', planData);
            if (!clarificationMessage) {
                console.warn('⚠️ clarification message missing data:', clarificationMessage);
                return;
            }
            const agentMessageData = {
                agent: AgentType.PLANNER,  // Changed from GROUP_CHAT_MANAGER to PLANNER (AP Manager)
                agent_type: AgentMessageType.AI_AGENT,
                timestamp: typeSafeAccess.number(clarificationMessage, 'timestamp', Date.now()),
                steps: [],   // intentionally always empty
                next_steps: [],  // intentionally always empty
                content: safeGet(clarificationMessage, 'data.question', ''),
                raw_data: JSON.stringify(safeGet(clarificationMessage, 'data', '')),
            } as AgentMessageData;
            console.log('✅ Parsed clarification message:', agentMessageData);
            setClarificationMessage(clarificationMessage.data as ParsedUserClarification | null);
            setAgentMessages(prev => [...prev, agentMessageData]);
            setShowBufferingText(false);
            setShowProcessingPlanSpinner(false);
            setSubmittingChatDisableInput(false);
            scrollToBottom();
            // Persist the agent message (this comes from WebSocket, so don't persist to prevent loop)
            processAgentMessage(agentMessageData, planData, false, '', true);

        });

        return () => unsubscribe();
    }, [scrollToBottom, planData, processAgentMessage]);
    //WebsocketMessageType.AGENT_TOOL_MESSAGE
    useEffect(() => {
        const unsubscribe = webSocketService.on(WebsocketMessageType.AGENT_TOOL_MESSAGE, (toolMessage: any) => {
            console.log('📋 Tool Message', toolMessage);
            // scrollToBottom()

        });

        return () => unsubscribe();
    }, [scrollToBottom]);

    //WebsocketMessageType.AGENT_RESULT - Handle final agent results
    useEffect(() => {
        const unsubscribe = webSocketService.on(WebsocketMessageType.AGENT_RESULT, (agentResult: any) => {
            console.log('📊 Agent Result received in PlanPage:', agentResult);
            
            // Extract agent info from the result message
            // The message structure is: { type, data: { type, data: { agent, result, status } }, timestamp }
            // due to double-wrapping in WebSocketService.emit()
            const innerData = agentResult?.data?.data || agentResult?.data || {};
            const agentName = innerData?.agent || agentResult?.data?.agent || 'Unknown Agent';
            const result = innerData?.result || agentResult?.data?.result || {};
            const status = innerData?.status || agentResult?.data?.status || 'completed';
            
            // Create a summary message from the result
            let content = '';
            if (result.summary) {
                content = result.summary;
            } else if (result.narrative) {
                content = result.narrative;
            } else if (result.analysis) {
                content = typeof result.analysis === 'string' ? result.analysis : JSON.stringify(result.analysis);
            } else if (typeof result === 'string') {
                content = result;
            } else {
                content = `${agentName} completed task (${status})`;
            }
            
            const agentMessageData: AgentMessageData = {
                agent: agentName,
                agent_type: AgentMessageType.AI_AGENT,
                timestamp: Date.now(),
                content: content,
                steps: [],
                next_steps: [],
                raw_data: JSON.stringify(agentResult),
                extra_data: result
            };
            
            console.log('📊 Adding agent result to messages:', agentMessageData);
            setAgentMessages(prev => [...prev, agentMessageData]);
        });

        return () => unsubscribe();
    }, []);

    //WebsocketMessageType.AGENT_MESSAGE
    useEffect(() => {
        console.log('🔄 AGENT_MESSAGE useEffect running - setting up listener');
        const unsubscribe = webSocketService.on(WebsocketMessageType.AGENT_MESSAGE, (agentMessage: any) => {
            console.log('📋 Agent Message received in PlanPage:', agentMessage)
            console.log('📋 Agent Message type:', typeof agentMessage);
            console.log('📋 Agent Message keys:', agentMessage ? Object.keys(agentMessage) : 'null');
            console.log('📋 Agent Message data:', agentMessage?.data);
            
            let agentMessageData: AgentMessageData | null = null;
            
            // Handle both parsed and unparsed message formats using safe property access
            const hasDirectAgent = SafePropertyAccess.hasRequiredAgentProperties(agentMessage);
            const hasNestedAgent = SafePropertyAccess.hasRequiredAgentProperties(safeGet(agentMessage, 'data', null));
            
            if (hasDirectAgent) {
                // Already parsed format: {agent: "...", content: "...", ...}
                agentMessageData = SafePropertyAccess.createSafeAgentMessage(agentMessage);
                console.log('✅ Received parsed agent message format');
            } else if (hasNestedAgent) {
                // Unparsed format: {data: {agent: "...", ...}, type: "agent_message"}
                console.log('⚠️ Received unparsed agent message format, converting...');
                const data = safeGet(agentMessage, 'data', {});
                agentMessageData = SafePropertyAccess.createSafeAgentMessage(data);
                agentMessageData.raw_data = JSON.stringify(agentMessage);
            }
            
            if (agentMessageData && typeSafeAccess.string(agentMessageData, 'agent', '').trim() !== '') {
                console.log('✅ Processing agent message for agent:', agentMessageData.agent);
                console.log('✅ Agent message content:', agentMessageData.content);
                const content = typeSafeAccess.string(agentMessageData, 'content', '');
                agentMessageData.content = PlanDataService.simplifyHumanClarification(content);
                setAgentMessages(prev => {
                    console.log('📋 Adding agent message to state. Previous count:', prev.length);
                    return [...prev, agentMessageData];
                });
                
                // CRITICAL FIX: Enhanced planner/manager completion detection
                // Check if this is a completion message from any planner/manager agent
                const agentNameLower = (agentMessageData.agent || '').toLowerCase();
                const isPlannerAgent = agentNameLower.includes('manager') || 
                    agentNameLower.includes('planner') || 
                    agentNameLower.includes('operations');
                const isPlannerCompletionMessage = isPlannerAgent && 
                    (content.includes('Workflow complete') || content.includes('🎉') || content.includes('completed successfully'));
                
                console.log('📋 Enhanced spinner logic check:', {
                    agent: agentMessageData.agent,
                    isPlannerCompletion: isPlannerCompletionMessage,
                    content: content.substring(0, 50) + '...'
                });
                
                if (!isPlannerCompletionMessage) {
                    setShowProcessingPlanSpinner(true);
                    console.log('📋 Setting processing spinner to true for agent:', agentMessageData.agent);
                    
                    // Set a timeout fallback to turn off spinner after 30 seconds if no completion message
                    if (spinnerTimeoutRef.current) {
                        clearTimeout(spinnerTimeoutRef.current);
                    }
                    spinnerTimeoutRef.current = setTimeout(() => {
                        console.log('⏰ Spinner timeout reached - forcing completion as fallback');
                        setShowProcessingPlanSpinner(false);
                        setWorkflowCompleted(true);
                        // Force history refresh on timeout
                        setReloadLeftList(prev => {
                            setTimeout(() => setReloadLeftList(true), 50);
                            return false;
                        });
                        spinnerTimeoutRef.current = null;
                    }, 30000); // 30 seconds timeout
                } else {
                    console.log('📋 Planner completion detected - processing completion');
                    setShowProcessingPlanSpinner(false);
                    setWorkflowCompleted(true);
                    setSubmittingChatDisableInput(false);
                    
                    // Update plan status to completed
                    setPlanData((prevPlanData: ProcessedPlanData | null) => {
                        if (prevPlanData?.plan) {
                            return {
                                ...prevPlanData,
                                plan: {
                                    ...prevPlanData.plan,
                                    overall_status: PlanStatus.COMPLETED
                                }
                            };
                        }
                        return prevPlanData;
                    });
                    
                    // Force history refresh
                    console.log('📋 Planner completion: Triggering history refresh...');
                    setReloadLeftList(prev => {
                        setTimeout(() => setReloadLeftList(true), 50);
                        return false;
                    });
                    
                    // Clear any pending timeout
                    if (spinnerTimeoutRef.current) {
                        clearTimeout(spinnerTimeoutRef.current);
                        spinnerTimeoutRef.current = null;
                    }
                    
                    console.log('📋 Planner completion processing finished');
                }
            } else {
                console.error('❌ Invalid agent message data:', agentMessage);
            }

        });

        return () => {
            console.log('🔄 AGENT_MESSAGE useEffect cleanup - unsubscribing');
            unsubscribe();
        };
    }, [scrollToBottom]); // Minimal dependencies to prevent re-registration

    //WebsocketMessageType.WORKFLOW_COMPLETED
    useEffect(() => {
        const unsubscribe = webSocketService.on(WebsocketMessageType.WORKFLOW_COMPLETED, (completionMessage: any) => {
            console.log('🎉 WORKFLOW_COMPLETED received - Enhanced Debug Info:');
            console.log('  - Message:', completionMessage);
            console.log('  - Current spinner state:', showProcessingPlanSpinner);
            console.log('  - Current workflow completed state:', workflowCompleted);
            console.log('  - Plan data exists:', !!planData);
            
            if (!completionMessage) {
                console.warn('⚠️ Workflow completion message missing data:', completionMessage);
                return;
            }
            
            // NOTE: Don't add another completion message here - the planner already sent
            // a comprehensive final summary via AGENT_MESSAGE. Adding another message here
            // causes duplicate "Workflow completed" messages in the UI.
            
            console.log('✅ Processing workflow completion (no duplicate message added)...');
            
            // CRITICAL: Handle workflow completion with multiple safeguards
            setShowBufferingText(false);
            setShowProcessingPlanSpinner(false);
            setSubmittingChatDisableInput(false);
            setWorkflowCompleted(true);
            // Don't add duplicate message: setAgentMessages(prev => [...prev, agentMessageData]);
            setSelectedTeam(planData?.team || null);
            
            // Clear any pending spinner timeout
            if (spinnerTimeoutRef.current) {
                clearTimeout(spinnerTimeoutRef.current);
                spinnerTimeoutRef.current = null;
            }
            
            console.log('🎉 WORKFLOW_COMPLETED: Spinner turned off, workflow marked complete');
            
            // Update plan status to completed
            if (planData?.plan) {
                const updatedPlanData = { ...planData };
                updatedPlanData.plan.overall_status = "completed";
                setPlanData(updatedPlanData);
                console.log('🎉 WORKFLOW_COMPLETED: Plan status updated to completed');
                console.log('🎉 WORKFLOW_COMPLETED: Plan data structure:', {
                    planId: updatedPlanData.plan?.id,
                    status: updatedPlanData.plan?.overall_status,
                    hasMessages: !!updatedPlanData.messages,
                    messageCount: updatedPlanData.messages?.length || 0
                });
            } else {
                console.warn('⚠️ WORKFLOW_COMPLETED: No plan data available to update status');
            }
            
            // CRITICAL: Force history refresh to save completed query
            console.log('🎉 WORKFLOW_COMPLETED: Triggering history refresh...');
            debouncedRefresh();
            
            // IMMEDIATE: Also trigger immediate refresh
            console.log('🎉 WORKFLOW_COMPLETED: Triggering immediate history refresh...');
            setReloadLeftList(true);
            
            // Debug: Check state immediately after setting
            setTimeout(() => {
                console.log('🔍 WORKFLOW_COMPLETED: reloadLeftList state after 100ms:', reloadLeftList);
            }, 100);
            
            // CRITICAL: Ensure plan data structure is correct for history persistence
            console.log('🎉 WORKFLOW_COMPLETED: Checking plan data for persistence...');
            console.log('🎉 WORKFLOW_COMPLETED: planData exists:', !!planData);
            console.log('🎉 WORKFLOW_COMPLETED: planData.plan exists:', !!planData?.plan);
            console.log('🎉 WORKFLOW_COMPLETED: planData.plan.id:', planData?.plan?.id);
            console.log('🎉 WORKFLOW_COMPLETED: current planId from URL:', planId);
            
            // CRITICAL FIX: Ensure plan data has correct structure for history display
            let effectivePlanData = planData;
            if (!planData?.plan?.id && planId) {
                console.log('🔧 WORKFLOW_COMPLETED: Plan data missing ID, reconstructing with URL planId');
                effectivePlanData = {
                    ...planData,
                    plan: {
                        ...planData?.plan,
                        id: planId,
                        overall_status: "completed"
                    }
                };
                // Update the state with corrected plan data
                setPlanData(effectivePlanData);
                console.log('🔧 WORKFLOW_COMPLETED: Plan data structure corrected');
            }
            
            // NOTE: Don't create a separate completion message here!
            // The planner already sends a comprehensive final summary via AGENT_MESSAGE
            // which includes "🎉 [Planner]: Workflow completed successfully! Here's what we discovered:"
            // Creating another message here causes duplicate "Workflow completed" messages in the UI.
            
            if (effectivePlanData?.plan?.id) {
                console.log('🎉 WORKFLOW_COMPLETED: Plan completion acknowledged, planId:', effectivePlanData.plan.id);
                // Don't persist a duplicate completion message - planner already sent one
            } else {
                console.warn('⚠️ WORKFLOW_COMPLETED: No plan data or plan ID available', {
                    hasPlanData: !!effectivePlanData,
                    hasPlan: !!effectivePlanData?.plan,
                    planId: effectivePlanData?.plan?.id,
                    urlPlanId: planId
                });
            }
            
            scrollToBottom();
            
            console.log('🎉 WORKFLOW_COMPLETED: All completion processing finished');
        });

        return () => unsubscribe();
    }, [scrollToBottom, planData, processAgentMessage, streamingMessageBuffer, setSelectedTeam, showProcessingPlanSpinner, workflowCompleted, debouncedRefresh]);

    // Loading message rotation effect
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (loading) {
            let index = 0;
            interval = setInterval(() => {
                index = (index + 1) % loadingMessages.length;
                setLoadingMessage(loadingMessages[index]);
            }, 3000);
        }
        return () => clearInterval(interval);
    }, [loading]);

    // WebSocket connection with proper error handling and v3 backend compatibility
    useEffect(() => {
        if (planId && continueWithWebsocketFlow) {
            console.log('🔌 Connecting WebSocket:', { planId, continueWithWebsocketFlow });

            const connectWebSocket = async () => {
                try {
                    await webSocketService.connect(planId);
                    console.log('✅ WebSocket connected successfully');
                } catch (error) {
                    console.error('❌ WebSocket connection failed:', error);
                    // Continue without WebSocket - the app should still work
                }
            };

            connectWebSocket();

            const handleConnectionChange = (connected: boolean) => {
                setWsConnected(connected);
                console.log('🔗 WebSocket connection status:', connected);
            };

            // handleStreamingMessage removed - agent messages are handled in separate useEffect

            const handlePlanApprovalResponse = (message: StreamMessage) => {
                console.log('✅ Plan approval response received:', message);
                if (message.data && message.data.approved) {
                    setPlanApproved(true);
                }
            };

            const handlePlanApprovalRequest = (message: StreamMessage) => {
                console.log('📥 Plan approval request received:', message);
                // This is handled by PlanChat component through its own listener
            };

            // Subscribe to all relevant v3 backend events
            const unsubscribeConnection = webSocketService.on('connection_status', (message) => {
                handleConnectionChange(message.data?.connected || false);
            });

            // Removed duplicate AGENT_MESSAGE handler - already handled in separate useEffect
            const unsubscribePlanApproval = webSocketService.on(WebsocketMessageType.PLAN_APPROVAL_RESPONSE, handlePlanApprovalResponse);
            const unsubscribePlanApprovalRequest = webSocketService.on(WebsocketMessageType.PLAN_APPROVAL_REQUEST, handlePlanApprovalRequest);
            const unsubscribeParsedPlanApprovalRequest = webSocketService.on(WebsocketMessageType.PLAN_APPROVAL_REQUEST, handlePlanApprovalRequest);

            return () => {
                console.log('🔌 Cleaning up WebSocket connections');
                unsubscribeConnection();
                // unsubscribeStreaming removed - no longer needed
                unsubscribePlanApproval();
                unsubscribePlanApprovalRequest();
                unsubscribeParsedPlanApprovalRequest();
                webSocketService.disconnect();
            };
        }
    }, [planId, loading, continueWithWebsocketFlow]);

    // Handle plan approval
    const handleApprovePlan = useCallback(async () => {
        if (!planApprovalRequest) return;

        setProcessingApproval(true);
        let id = showToast("Submitting Approval", "progress");
        try {
            // Import getUserId to ensure we send the same user_id that the WebSocket is connected with
            const { getUserId } = await import('../api/config');
            const userId = getUserId();
            // Convert to default_user if it's the placeholder UUID (same logic as WebSocketService)
            const finalUserId = (userId && userId !== "00000000-0000-0000-0000-000000000000") ? userId : "default_user";
            
            console.log('📤 Approving plan with user_id:', finalUserId);
            console.log('📤 WebSocket connected:', wsConnected);
            console.log('📤 Plan ID:', planId);
            
            // Ensure WebSocket is connected before sending approval
            // This helps ensure we receive real-time agent messages
            if (!wsConnected && planId) {
                console.log('📤 WebSocket not connected, attempting to connect...');
                try {
                    await webSocketService.connect(planId);
                    console.log('📤 WebSocket connected successfully before approval');
                    // Small delay to ensure connection is fully established
                    await new Promise(resolve => setTimeout(resolve, 100));
                } catch (wsError) {
                    console.warn('📤 WebSocket connection failed, proceeding with approval anyway:', wsError);
                }
            }
            
            await apiService.approvePlan({
                m_plan_id: planApprovalRequest.id,
                plan_id: planData?.plan?.id,
                approved: true,
                feedback: 'Plan approved by user',
                user_id: finalUserId
            });

            dismissToast(id);
            setShowProcessingPlanSpinner(true);
            setShowApprovalButtons(false);

        } catch (error) {
            dismissToast(id);
            showToast("Failed to submit approval", "error");
            console.error('❌ Failed to approve plan:', error);
        } finally {
            setProcessingApproval(false);
        }
    }, [planApprovalRequest, planData, setProcessingApproval, wsConnected, planId]);

    // Handle plan rejection  
    const handleRejectPlan = useCallback(async () => {
        if (!planApprovalRequest) return;

        setProcessingApproval(true);
        let id = showToast("Submitting cancellation", "progress");
        try {
            // Import getUserId to ensure we send the same user_id that the WebSocket is connected with
            const { getUserId } = await import('../api/config');
            const userId = getUserId();
            // Convert to default_user if it's the placeholder UUID (same logic as WebSocketService)
            const finalUserId = (userId && userId !== "00000000-0000-0000-0000-000000000000") ? userId : "default_user";
            
            await apiService.approvePlan({
                m_plan_id: planApprovalRequest.id,
                plan_id: planData?.plan?.id,
                approved: false,
                feedback: 'Plan rejected by user',
                user_id: finalUserId
            });

            dismissToast(id);

            navigate('/');

        } catch (error) {
            dismissToast(id);
            showToast("Failed to submit cancellation", "error");
            console.error('❌ Failed to reject plan:', error);
            navigate('/');
        } finally {
            setProcessingApproval(false);
        }
    }, [planApprovalRequest, planData, navigate, setProcessingApproval]);
    // Chat submission handler - updated for v3 backend compatibility

    const handleOnchatSubmit = useCallback(
        async (chatInput: string) => {
            if (!chatInput.trim()) {
                showToast("Please enter a clarification", "error");
                return;
            }
            setInput("");

            if (!planData?.plan) return;
            setSubmittingChatDisableInput(true);
            let id = showToast("Submitting clarification", "progress");

            try {
                // Use legacy method for non-v3 backends
                const response = await PlanDataService.submitClarification({
                    request_id: clarificationMessage?.request_id || "",
                    answer: chatInput,
                    plan_id: planData?.plan.id,
                    m_plan_id: planApprovalRequest?.id || ""
                });

                console.log("Clarification submitted successfully:", response);
                setInput("");
                dismissToast(id);
                showToast("Clarification submitted successfully", "success");

                const agentMessageData = {
                    agent: 'human',
                    agent_type: AgentMessageType.HUMAN_AGENT,
                    timestamp: Date.now(),
                    steps: [],   // intentionally always empty
                    next_steps: [],  // intentionally always empty
                    content: chatInput || '',
                    raw_data: chatInput || '',
                } as AgentMessageData;

                setAgentMessages(prev => [...prev, agentMessageData]);
                setSubmittingChatDisableInput(true);
                setShowProcessingPlanSpinner(true);
                scrollToBottom();

            } catch (error: any) {
                setShowProcessingPlanSpinner(false);
                dismissToast(id);
                setSubmittingChatDisableInput(false);
                showToast(
                    "Failed to submit clarification",
                    "error"
                );

            } finally {

            }
        },
        [planData?.plan, showToast, dismissToast, loadPlanData]
    );


    // ✅ Handlers for PlanPanelLeft with plan cancellation protection
    const handleNewTaskButton = useCallback(() => {
        handleNavigationWithAlert(() => {
            navigate("/", { state: { focusInput: true } });
        });
    }, [navigate, handleNavigationWithAlert]);


    const resetReload = useCallback(() => {
        console.log('📋 resetReload called - left panel finished reloading');
        console.trace('📋 resetReload call stack:');
        setReloadLeftList(false);
    }, []);

    useEffect(() => {
        const initializePlanLoading = async () => {
            if (!planId) {
                resetPlanVariables();
                setErrorLoading(true);
                return;
            }

            // Connect WebSocket first to receive real-time updates
            setContinueWithWebsocketFlow(true);
            
            try {
                // Try to load existing plan data, but don't fail if it doesn't exist yet
                // The plan might still be being created and will arrive via WebSocket
                await loadPlanData(false);
            } catch (err) {
                console.log("Plan not found yet, waiting for WebSocket notification:", err);
                // Don't set error loading - the plan might still be being created
                // Just set loading state and wait for WebSocket
                setLoading(true);
                setWaitingForPlan(true);
            }
        };

        initializePlanLoading();

        // Cleanup function to clear any pending timeouts
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
        };
    }, [planId, loadPlanData, resetPlanVariables, setErrorLoading]);

    if (errorLoading) {
        return (
            <CoralShellColumn>
                <CoralShellRow>
                    <PlanPanelLeft
                        reloadTasks={reloadLeftList}
                        onNewTaskButton={handleNewTaskButton}
                        restReload={resetReload}
                        onTeamSelect={() => { }}
                        onTeamUpload={async () => { }}
                        isHomePage={false}
                        selectedTeam={selectedTeam}
                        onNavigationWithAlert={handleNavigationWithAlert}
                    />
                    <Content>
                        <div className="plan-error-message">
                            <Text size={500}>
                                {"An error occurred while loading the plan"}
                            </Text>
                        </div>
                    </Content>
                </CoralShellRow>
            </CoralShellColumn>
        );
    }

    return (
        <CoralShellColumn>
            <CoralShellRow>
                {/* ✅ RESTORED: PlanPanelLeft for navigation */}
                <PlanPanelLeft
                    reloadTasks={reloadLeftList}
                    onNewTaskButton={handleNewTaskButton}
                    restReload={resetReload}
                    onTeamSelect={() => { }}
                    onTeamUpload={async () => { }}
                    isHomePage={false}
                    selectedTeam={selectedTeam}
                    onNavigationWithAlert={handleNavigationWithAlert}
                />

                <Content>
                    {loading || !planData ? (
                        <>
                            <div className="plan-loading-spinner">
                                <Spinner size="medium" />
                                <Text>Loading plan data...</Text>
                            </div>
                            <LoadingMessage
                                loadingMessage={loadingMessage}
                                iconSrc={Octo}
                            />
                        </>
                    ) : (
                        <>
                            <ContentToolbar
                                panelTitle="Exception Workspace"
                            >
                                {/* <PanelRightToggles>
                                    <TaskListSquareLtr />
                                </PanelRightToggles> */}
                            </ContentToolbar>

                            {/* Main workspace with sidebars */}
                            <div style={{ 
                                display: 'flex', 
                                flex: 1, 
                                gap: '16px', 
                                padding: '0 16px',
                                overflow: 'hidden'
                            }}>
                                {/* Left sidebar - System Integrations */}
                                <div style={{ 
                                    flexShrink: 0,
                                    paddingTop: '32px'
                                }}>
                                    <IntegrationsSidebar team={selectedTeam} />
                                </div>

                                {/* Center - Chat area */}
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                    <PlanChat
                                        planData={planData}
                                        OnChatSubmit={handleOnchatSubmit}
                                        loading={loading}
                                        setInput={setInput}
                                        submittingChatDisableInput={submittingChatDisableInput}
                                        input={input}
                                        streamingMessages={streamingMessages}
                                        wsConnected={wsConnected}
                                        onPlanApproval={(approved) => setPlanApproved(approved)}
                                        planApprovalRequest={planApprovalRequest}
                                        waitingForPlan={waitingForPlan}
                                        messagesContainerRef={messagesContainerRef}
                                        streamingMessageBuffer={streamingMessageBuffer}
                                        showBufferingText={showBufferingText}
                                        agentMessages={agentMessages}
                                        showProcessingPlanSpinner={showProcessingPlanSpinner}
                                        showApprovalButtons={showApprovalButtons}
                                        processingApproval={processingApproval}
                                        handleApprovePlan={handleApprovePlan}
                                        handleRejectPlan={handleRejectPlan}
                                        selectedTeam={selectedTeam}
                                    />
                                </div>

                                {/* Right sidebar - AI Models */}
                                <div style={{ 
                                    flexShrink: 0,
                                    paddingTop: '32px'
                                }}>
                                    <ModelsSidebar isAgentActive={showProcessingPlanSpinner || showBufferingText} />
                                </div>
                            </div>
                        </>
                    )}
                </Content>

                <PlanPanelRightTabs
                    planData={planData}
                    loading={loading}
                    planApprovalRequest={planApprovalRequest}
                    agentMessages={agentMessages}
                    selectedTeam={selectedTeam}
                />
            </CoralShellRow>

            {/* Plan Cancellation Confirmation Dialog */}
            <PlanCancellationDialog
                isOpen={showCancellationDialog}
                onConfirm={handleConfirmCancellation}
                onCancel={handleCancelDialog}
                loading={cancellingPlan}
            />
        </CoralShellColumn>
    );
};

export default PlanPage;